import React, { createRef, useRef, useState } from 'react'
import AgTechPopUp from '../../Portals/Popups/AgTechPopUp';
import { v4 as uuidv4 } from 'uuid';
import { getAgTechLayerCount } from '../../Portals/AgTechPortal';
import { AgTechPopUpCoreProps, AgTechPopUpContext, AgTechPopUpRendering, AgTechRenderedPopUp, AgTechPopUpLayer } from '@agtech/core/functions/AgTechAppPopUps';

/// Types

export type AgTechPopUpRenderProps = AgTechPopUpCoreProps & {
    callerRef: React.RefObject<any>
}

/// Manager

export type AgTechShowPopUpProps = AgTechPopUpRenderProps & { parentPopUpContext: AgTechPopUpContext }

export type AgTechPopUpManager = {
    popups: AgTechPopUpRendering[],
    showPopUp: (props: AgTechShowPopUpProps) => AgTechRenderedPopUp,
    closeAll: () => void
}

export const DefaultAgTechPopUpManager: AgTechPopUpManager = {
    popups: [],
    showPopUp: (popup: AgTechPopUpRenderProps) => {
        console.error('Attempted to show popup with default app context');
        return {
            callerRef: createRef(),
            popupRef: createRef(),
            layer: 0,
            close: () => {}
        }
    },
    closeAll: () => {
        console.error('Attempted to close all popups from the default popup context');
    }
}

export const usePopups = (): AgTechPopUpManager => {
    let [popups, updatePopups] = useState<AgTechPopUpRendering[]>([]);
    let renderedPopUps = useRef(popups);

    return {
        popups: popups,
        showPopUp: (popupRenderProps: AgTechShowPopUpProps) => {     
            const popupKey = uuidv4();
            const popupRef = createRef<HTMLDivElement>();
            const layer = getPopUpLayer(popupRenderProps);

            let popupRendering: AgTechPopUpRendering = {
                popupId: popupKey,
                callerRef: popupRenderProps.callerRef,
                popupRef: popupRef,
                popup: popupRenderProps.popup,
                popupConfig: {
                    ...popupRenderProps.config
                },
                layer: layer,
                parentPopUpContext: popupRenderProps.parentPopUpContext,
                closePopup: (popupId: string) => {
                    if (popupId !== popupKey) {
                        return;
                    }

                    renderedPopUps.current = renderedPopUps.current.filter(dg => dg.popupId !== popupKey);
                    updatePopups(renderedPopUps.current);

                    if (popupRenderProps.handlers && popupRenderProps.handlers.onPopUpClosed) {
                        popupRenderProps.handlers.onPopUpClosed();
                    }
                }
            };

            // Close all popups in the current or lower layers
            if ((popupRenderProps.config?.shouldCloseOthersOnOpen) ?? true) {
                renderedPopUps.current = [...renderedPopUps.current.filter(pop => {
                    return pop.layer.agtechLayer < layer.agtechLayer ||
                           pop.layer.popupLayer < layer.popupLayer;
                })];

                updatePopups(renderedPopUps.current);
            }

            if (popupRenderProps.handlers && popupRenderProps.handlers.onBeforePopUpOpened) {
                popupRenderProps.handlers.onBeforePopUpOpened();
            }

            renderedPopUps.current = [...renderedPopUps.current, popupRendering];
            updatePopups(renderedPopUps.current);

            if (popupRenderProps.handlers && popupRenderProps.handlers.onPopUpOpened) {
                popupRenderProps.handlers.onPopUpOpened();
            }

            return {
                callerRef: popupRenderProps.callerRef,
                popupRef: popupRef,
                close: () => popupRendering.closePopup(popupKey)
            }
        },
        closeAll: () => {
            renderedPopUps.current = [];
            updatePopups(renderedPopUps.current);
        }
    }
}

const getPopUpLayer = (props: AgTechShowPopUpProps): AgTechPopUpLayer => {
    const agtechLayer = getAgTechLayerCount();

    return {
        agtechLayer: agtechLayer,
        popupLayer: props.parentPopUpContext && props.parentPopUpContext.caller.current
            ? props.parentPopUpContext.layer.popupLayer + 1
            : agtechLayer
    };
}

/// Components

export type AgTechPopUpSurfaceProps = {
    popupManager: AgTechPopUpManager
}

const AgTechPopUpSurface = (props: AgTechPopUpSurfaceProps) => {
    return (
        <>
           { props.popupManager.popups.map((popupRendering: AgTechPopUpRendering) => {
                return (
                    <AgTechPopUp
                        key={popupRendering.popupId}
                        ref={popupRendering.popupRef}
                        {...popupRendering}
                    />
                )
            })}
        </>
    );
};

export default AgTechPopUpSurface;